import { Inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';

/**
 * @internal
 * Manages a single document-level `focusin` listener shared across all open modals.
 * Maintains a stack of modal window elements and redirects focus to the topmost
 * modal when focus lands outside of it (e.g. when the user tabs back into the page).
 */
@Injectable({ providedIn: 'root' })
export class ModalFocusService implements OnDestroy {
  private readonly stack: HTMLElement[] = [];
  private focusInSubscription: Subscription | null = null;

  constructor(
    // cannot use type `Document` due to compilation issues caused by `strictMetadataEmit`
    @Inject(DOCUMENT) private readonly document: any,
  ) {}

  register(modalWindowEl: HTMLElement): void {
    this.stack.push(modalWindowEl);
    if (this.stack.length === 1) {
      this.startListening();
    }
  }

  deregister(modalWindowEl: HTMLElement): void {
    const index = this.stack.indexOf(modalWindowEl);
    if (index > -1) {
      this.stack.splice(index, 1);
    }
    if (this.stack.length === 0) {
      this.stopListening();
    }
  }

  ngOnDestroy(): void {
    this.stopListening();
    this.stack.length = 0;
  }

  private startListening(): void {
    this.focusInSubscription = fromEvent<FocusEvent>(this.document, 'focusin').subscribe((event) => {
      const topmost = this.stack[this.stack.length - 1];
      if (topmost && !topmost.contains(event.target as Node)) {
        topmost.focus();
      }
    });
  }

  private stopListening(): void {
    this.focusInSubscription?.unsubscribe();
    this.focusInSubscription = null;
  }
}
